import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ApiShirt } from "../types/Shirt";
import { useAuth } from "./AuthContext";
import { auctionService, type Auction } from "../services/auctionService";
import { fetchShirtById } from "../services/shirtService";
import { fetchCurrentOrders, fetchCompletedOrders } from "../services/orderService";
import { trackAddToCart, itemFromShirt } from "../lib/analytics";

const STORAGE_KEY = "fsm_cart";

export interface CartItem {
  shirtId: string;
  shirt: ApiShirt;
  addedAt: number;
}

export interface WonAuctionCartItem {
  auction: Auction;
  shirt: ApiShirt;
}

interface CartContextValue {
  items: CartItem[];
  wonAuctionItems: WonAuctionCartItem[];
  cartCount: number;
  addToCart: (shirt: ApiShirt) => void;
  removeFromCart: (shirtId: string) => void;
  clearCart: () => void;
  isInCart: (shirtId: string) => boolean;
  refreshWonAuctions: () => void;
}

const CartContext = createContext<CartContextValue>({
  items: [],
  wonAuctionItems: [],
  cartCount: 0,
  addToCart: () => {},
  removeFromCart: () => {},
  clearCart: () => {},
  isInCart: () => false,
  refreshWonAuctions: () => {},
});

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as CartItem[];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function clearStoredCart() {
  localStorage.removeItem(STORAGE_KEY);
}

function hasActiveOrder(status: string | null | undefined): boolean {
  const normalized = String(status || "").toLowerCase();
  return [
    "processing",
    "pending",
    "shipped",
    "delivered",
    "completed",
    "paid",
  ].includes(normalized);
}

function hasActivePayment(status: string | null | undefined): boolean {
  const normalized = String(status || "").toLowerCase();
  return [
    "processing",
    "pending",
    "requires_action",
    "requires_confirmation",
    "requires_capture",
    "paid",
    "succeeded",
    "completed",
    "captured",
  ].includes(normalized);
}

function hasSuccessfulPayment(status: string | null | undefined): boolean {
  const normalized = String(status || "").toLowerCase();
  return ["paid", "succeeded", "success", "completed", "captured"].includes(
    normalized,
  );
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { isAuthed } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [wonAuctionItems, setWonAuctionItems] = useState<WonAuctionCartItem[]>(
    [],
  );
  // Prevents the saveCart effect from firing on the very first render
  // (when items is still []) and overwriting localStorage before loadCart runs.
  const cartLoadedRef = useRef(false);

  useEffect(() => {
    if (isAuthed) {
      cartLoadedRef.current = false;
      const stored = loadCart();
      cartLoadedRef.current = true;
      setItems(stored);
      return;
    }

    cartLoadedRef.current = false;
    setItems([]);
    clearStoredCart();
  }, [isAuthed]);

  useEffect(() => {
    if (!cartLoadedRef.current) return; // don't save before the initial load
    if (isAuthed) {
      saveCart(items);
      return;
    }

    clearStoredCart();
  }, [items, isAuthed]);

  // Fetch won auctions + sanitize cart using the new unified orders API
  const refreshWonAuctions = useCallback(() => {
    if (!isAuthed) {
      setWonAuctionItems([]);
      return;
    }
    Promise.all([
      auctionService.getWonAuctions(),
      fetchCurrentOrders().catch(() => ({ action_required: [], winning: [], outbid: [] })),
      fetchCompletedOrders().catch(() => []),
    ])
      .then(async ([auctions, current, completed]) => {
        // Collect shirt IDs that already have an active or completed order
        const activeShirtIds = new Set([
          ...current.action_required.map((o) => o.shirt.id),
          ...current.winning.map((o) => o.shirt.id),
          ...current.outbid.map((o) => o.shirt.id),
          ...completed.map((o) => o.shirt.id),
        ]);

        // Sanitize cart: remove fixed-price items that are already ordered
        if (activeShirtIds.size > 0) {
          setItems((prev) =>
            prev.filter((item) => !activeShirtIds.has(item.shirtId)),
          );
        }

        if (auctions.length === 0) {
          setWonAuctionItems([]);
          return;
        }

        // Won auctions that are already paid show up in completed orders
        const paidShirtIds = new Set(completed.map((o) => o.shirt.id));

        const enriched = await Promise.all(
          auctions.map(async (auction) => {
            if (paidShirtIds.has(auction.shirt_id)) {
              return null;
            }
            try {
              const shirt = await fetchShirtById(auction.shirt_id);
              return { auction, shirt } as WonAuctionCartItem;
            } catch {
              return null;
            }
          }),
        );
        setWonAuctionItems(
          enriched.filter((e): e is WonAuctionCartItem => e !== null),
        );
      })
      .catch(() => setWonAuctionItems([]));
  }, [isAuthed]);

  useEffect(() => {
    refreshWonAuctions();
  }, [refreshWonAuctions]);

  const addToCart = useCallback(
    (shirt: ApiShirt) => {
      if (!isAuthed) return;

      let added = false;
      setItems((prev) => {
        if (prev.some((i) => i.shirtId === shirt.id)) return prev;
        added = true;
        return [...prev, { shirtId: shirt.id, shirt, addedAt: Date.now() }];
      });
      // add_to_cart enkel pushen wanneer het shirt echt is toegevoegd.
      if (added) trackAddToCart(itemFromShirt(shirt));
    },
    [isAuthed],
  );

  const removeFromCart = useCallback(
    (shirtId: string) => {
      if (!isAuthed) return;
      setItems((prev) => {
        const next = prev.filter((i) => i.shirtId !== shirtId);
        // Write synchronously so the removal survives an immediate page
        // navigation (e.g. redirect to Stripe) before the effect can run.
        saveCart(next);
        return next;
      });
    },
    [isAuthed],
  );

  const clearCart = useCallback(() => {
    if (!isAuthed) return;
    setItems([]);
  }, [isAuthed]);

  const isInCart = useCallback(
    (shirtId: string) => items.some((i) => i.shirtId === shirtId),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      wonAuctionItems,
      cartCount: items.length + wonAuctionItems.length,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
      refreshWonAuctions,
    }),
    [items, wonAuctionItems, addToCart, removeFromCart, clearCart, isInCart, refreshWonAuctions],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
